import React, { useState, useEffect, useRef } from 'react';
import styles from './File.module.scss';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { getSubdomain } from '../../../../../../utils/utils';

import {
    downloadDarkGray,
    downloadIconDataRoom,
    downloadIconDataRoomHovered,
    fileIconDataRoom,
    PURPLE_THREE_DOTS_VERTICAL,
} from '../../../../../../assets/hostedassets';
import threeDotVertical from '../../../../../../assets/three-dot-vertical.svg';
import useFetchDataRoomFiles from '../../../../useFetchDataRoomFiles';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import DocumentViewer from '../../../../../../common/DocumentViewer/DocumentViewer';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../../../app/hooks';
import {
    AICA_DATAROOM_FILE_ARCHIVE,
    AICA_DATAROOM_FILE_DELETE,
    AICA_DATAROOM_FILE_DOWNLOAD,
    AICA_DATAROOM_FILE_MOVE,
} from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { FILE_FOLDER_TYPES } from '../../../../../../store/investor/constants';
import { isLenderType } from '../../../../../../utils/utils';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';

const File = ({
    name,
    lastUpdated,
    index,
    docId,
    masterFolderName,
    fetchFiles,
    setShowDataRoomModal,
    isNew,
    startDate,
    endDate,
    selectedItems = new Set(),
    onItemSelect,
    showCheckboxes = false,
    // isRecurCompany,
}) => {
    const [downloadImgUrl, setDownloadImgUrl] = useState(downloadIconDataRoomHovered);
    const [showOtherOptions, setShowOtherOptions] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const dropdownRef: any = useRef(null);
    const threeDotsRef: any = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const fileViewType = searchParams.get('fileViewType');
    const isLender = isLenderType();
    const isArchiveView = fileViewType === 'archived';

    const {
        handleFileClick,
        showFile,
        setShowFile,
        fileUrl,
        downloadTheFile,
        handleDeleteFile,
        handleUnarchiveFileBulk,
        getFormattedDate,
        downloadingFile,
        MoveToActive,
        DeleteButton,
        MoveButton,
        ArchiveButton,
    } = useFetchDataRoomFiles(fetchFiles);

    const user = useAppSelector((state) => state.user.userData);
    const selectedCompany = useAppSelector((state) => state.investor.selectedCompany);
    const isPartner = user?.accountType === USER_TYPE.PARTNER;
    const isLenderOrPartner = isLender || isPartner;

    const fileId = docId ? String(docId) : name;
    const isSelected = selectedItems.has(fileId);

    const calculateDropdownPosition = () => {
        if (threeDotsRef.current) {
            //@ts-ignore
            const rect = threeDotsRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX - 50,
            });
        }
    };

    useEffect(() => {
        if (showOtherOptions) {
            calculateDropdownPosition();
        }
    }, [showOtherOptions]);

    useEffect(() => {
        const handleScroll = () => {
            if (showOtherOptions) {
                setShowOtherOptions(false);
            }
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [showOtherOptions]);

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                threeDotsRef.current &&
                !threeDotsRef.current?.contains(event.target)
            ) {
                setShowOtherOptions(false);
            }
        };
        if (showOtherOptions) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showOtherOptions]);

    const dropdown = showOtherOptions
        ? createPortal(
              <div ref={dropdownRef} className={styles.OtherOptions} style={{ top: dropdownPosition.top, left: dropdownPosition.left }}>
                  {isArchiveView ? (
                      <>
                          <MoveToActive
                              docId={docId}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleUnarchiveFileBulk={handleUnarchiveFileBulk}
                          />
                          <DeleteButton
                              docId={docId}
                              name={name}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleDeleteFile={handleDeleteFile}
                              isArchive={false}
                          />
                      </>
                  ) : (
                      <>
                          <MoveButton
                              docId={docId}
                              masterFolderName={masterFolderName}
                              setShowOtherOptions={setShowOtherOptions}
                              setShowDataRoomModal={setShowDataRoomModal}
                              user={user}
                              selectedCompany={selectedCompany}
                          />
                          <DeleteButton
                              docId={docId}
                              name={name}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleDeleteFile={handleDeleteFile}
                              isArchive={false}
                          />
                          <ArchiveButton
                              docId={docId}
                              name={name}
                              masterFolderName={masterFolderName}
                              fetchFiles={fetchFiles}
                              setShowOtherOptions={setShowOtherOptions}
                              user={user}
                              selectedCompany={selectedCompany}
                              handleDeleteFile={handleDeleteFile}
                          />
                      </>
                  )}
              </div>,
              document.body,
          )
        : null;

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (onItemSelect) {
            onItemSelect(fileId);
        }
    };

    return (
        <div className={isLenderOrPartner ? styles.fileLenderView : styles.file}>
            <div className={styles.nameCell}>
                <div
                    style={{
                        marginLeft: `${index * 1.25}rem`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                    onClick={async () => {
                        if (docId) await handleFileClick(docId, name);
                    }}
                >
                    {showCheckboxes && (
                        <>
                            <span style={{ width: '0.75rem', display: 'inline-block' }}></span>
                            <div className={styles.checkboxCell} onClick={handleCheckboxClick}>
                                <input type="checkbox" checked={isSelected} onChange={handleCheckboxChange} className={styles.checkbox} />
                            </div>
                        </>
                    )}
                    <span className={styles.FileIconContainer}>
                        <img src={fileIconDataRoom} alt={'folder'} />
                    </span>
                    <span className={styles.FileName} title={name} aria-label={name}>
                        {name}
                    </span>
                </div>
            </div>
            <span className={isNew ? styles.labelStatus : ''}>{isNew ? 'New' : ''}</span>
            <span className={startDate && styles.DateContainer}>{getFormattedDate('file', startDate)}</span>
            <span className={endDate && styles.DateContainer}>{getFormattedDate('file', endDate)}</span>
            <span className={styles.FileDate}>{lastUpdated}</span>
            {!isLenderOrPartner && (
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        if (docId) downloadTheFile(docId, name);

                        Mixpanel.track(AICA_DATAROOM_FILE_DOWNLOAD, {
                            userRole: user?.accountType,
                            pnoPlatform: getSubdomain(),
                            userId: user?.userId,
                            currentUrl: window.location.href,
                            organizationId: selectedCompany?.assesseeOrgId,
                            fileType: masterFolderName,
                        });
                    }}
                    className={styles.downloadIconContainer}
                    onMouseEnter={() => setDownloadImgUrl(downloadIconDataRoomHovered)}
                    onMouseLeave={() => setDownloadImgUrl(downloadIconDataRoomHovered)}
                >
                    {downloadingFile ? (
                        <LoadingSpinner color="#6021B3" style={{ margin: '0', padding: '0' }} />
                    ) : (
                        <img src={downloadImgUrl} alt={'icon'} />
                    )}
                </span>
            )}
            {/* {!isLenderOrPartner && <div className={styles.separator} />} */}
            {!isLenderOrPartner && (
                <span
                    ref={threeDotsRef}
                    className={styles.ThreeDots}
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowOtherOptions(!showOtherOptions);
                    }}
                >
                    <img src={threeDotVertical} style={{ cursor: 'pointer', width: '30px', height: '30px' }} alt={'More'} />
                </span>
            )}
            {dropdown}
            {showFile && (
                <SideDrawer heading={name} showHeading={true} width="75%" allowClose onClose={() => setShowFile(false)}>
                    <DocumentViewer fileUrl={fileUrl} />
                </SideDrawer>
            )}
        </div>
    );
};

export default File;
